/**
 * Stream B — B8: GDPR Consent Manager
 *
 * Track consent per contact per purpose. Real API, no mocks.
 * Export consent records for auditors.
 * Consent expiry tracking (re-consent after 24 months).
 */
import { useState, useEffect, useCallback } from "react";
import { FileCheck, Download, Search, AlertTriangle } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { SkeletonCard } from "@/components/skeleton";
import {
  CompliancePageHeader,
  ComplianceTenantRequiredState,
  useComplianceTenant,
} from "./shared";

interface ConsentRecord {
  id: string;
  personId: string;
  purpose: string;
  source: string;
  lawfulBasis: string;
  grantedAt: string;
  withdrawnAt: string | null;
  policyVersion: string;
  updatedAt: string;
}

const CONSENT_TYPES = ["marketing_email", "marketing_sms", "marketing_whatsapp", "analytics", "profiling", "third_party_sharing"] as const;

const STATUS_STYLES = {
  active: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  revoked: "bg-[var(--color-error)]/10 text-[var(--color-error)]",
  expired: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  pending: "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]",
};

function getConsentStatus(record: ConsentRecord): "active" | "revoked" | "expired" | "pending" {
  if (record.withdrawnAt) return "revoked";
  // Check 24-month expiry
  const grantedDate = new Date(record.grantedAt);
  const expiryDate = new Date(grantedDate.getTime() + 24 * 30 * 24 * 60 * 60 * 1000); // ~24 months
  if (Date.now() > expiryDate.getTime()) return "expired";
  return "active";
}

function daysUntilExpiry(record: ConsentRecord): number | null {
  if (record.withdrawnAt) return null;
  const grantedDate = new Date(record.grantedAt);
  const expiryDate = new Date(grantedDate.getTime() + 24 * 30 * 24 * 60 * 60 * 1000);
  return Math.max(0, Math.floor((expiryDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
}

export default function ConsentDashboardPage() {
  const { tenantId, hasTenant } = useComplianceTenant();
  const [records, setRecords] = useState<ConsentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setRecords([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    apiFetch<{ data: ConsentRecord[] }>(`/platform/compliance/consent-records?tenant_id=${tenantId}`)
      .then((res) => setRecords(res.data))
      .catch((err: unknown) => {
        setRecords([]);
        setError(err instanceof Error ? err.message : "Failed to load consent records");
      })
      .finally(() => setIsLoading(false));
  }, [tenantId]);

  const filtered = records.filter((r) => {
    if (typeFilter !== "all" && r.purpose !== typeFilter) return false;
    if (searchQuery && !r.personId.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const expiringSoon = records.filter((r) => {
    const days = daysUntilExpiry(r);
    return days !== null && days < 90;
  });

  const handleExportCsv = useCallback(() => {
    const headers = ["Person ID", "Type", "Method", "Lawful Basis", "Status", "Granted At", "Revoked At"];
    const rows = filtered.map((r) => [
      r.personId,
      r.purpose,
      r.source,
      r.lawfulBasis,
      getConsentStatus(r),
      r.grantedAt,
      r.withdrawnAt ?? "",
    ]);
    const escape = (v: string) => v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
    const csv = [headers.join(","), ...rows.map((row) => row.map(escape).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `consent-records-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <CompliancePageHeader
          icon={<FileCheck className="h-5 w-5 text-[var(--color-primary)]" />}
          title="Consent Records"
          description="Reads the control-plane GDPR consent ledger for the active organization."
        />
        <button
          onClick={handleExportCsv}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      {!hasTenant ? <ComplianceTenantRequiredState /> : null}

      {error ? (
        <div className="rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {/* Expiry warning */}
      {expiringSoon.length > 0 && (
        <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 px-4 py-3 flex items-center gap-2 text-sm text-[var(--color-warning)]">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {expiringSoon.length} consent record{expiringSoon.length > 1 ? "s" : ""} expiring within 90 days — re-consent required.
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by contact..."
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] py-2 pl-9 pr-3 text-sm text-[var(--color-foreground)]"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          <option value="all">All types</option>
          {CONSENT_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} className="h-12" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <FileCheck className="h-10 w-10 mx-auto text-[var(--color-muted-foreground)] mb-3" />
          <p className="text-[var(--color-foreground)] font-medium">No consent records yet</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Records appear as contacts grant consent via forms, imports, or manual entry.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--color-border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Expiry</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {filtered.map((r) => {
                const status = getConsentStatus(r);
                const days = daysUntilExpiry(r);
                return (
                  <tr key={r.id} className="stagger-item hover:bg-[var(--color-surface)]/50">
                    <td className="px-4 py-3 text-[var(--color-foreground)] font-mono text-xs truncate max-w-[120px]">{r.personId.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-[var(--color-foreground)]">{r.purpose.replaceAll("_", " ")}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>{status}</span>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{r.source}</td>
                    <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{formatDate(r.grantedAt)}</td>
                    <td className="px-4 py-3">
                      {days !== null ? (
                        <span className={days < 90 ? "text-[var(--color-warning)] font-medium" : "text-[var(--color-muted-foreground)]"}>
                          {days}d
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
